import { useState } from 'react'
import { ReactNode } from 'react'
import { Accordion as MuiAccordion, AccordionSummary, AccordionDetails } from '@mui/material'
import styled from 'styled-components'

import { theme, NAV_HEIGHT } from '~/styles'

import { Button } from './Button'
import { Tooltip } from './Tooltip'
import { useTranslation } from 'react-i18next'

enum AccordionSizeEnum {
  medium = 'medium',
  large = 'large',
}

type AccordionSize = keyof typeof AccordionSizeEnum

interface AccordionProps {
  id?: string
  summary: ReactNode
  children: ReactNode | ((args: { isOpen: boolean }) => ReactNode)
  initiallyOpen?: boolean
  size?: AccordionSize
  noContentMargin?: boolean
}

export const Accordion = ({
  id,
  summary,
  children,
  initiallyOpen = false,
  size = AccordionSizeEnum.medium,
  noContentMargin = false,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen)
  const { t } = useTranslation()

  return (
    <Container id={id}>
      <StyledAccordion expanded={isOpen} onChange={(_, expanded) => setIsOpen(expanded)} square>
        <Summary $size={size}>
          <Tooltip
            placement="top-start"
            title={t(
              isOpen
                ? 'customer.detail.wallet_tooltip_collapse'
                : 'customer.detail.wallet_tool_expand'
            )}
          >
            <Button
              tabIndex={-1}
              data-test="open-charge"
              variant="quaternary"
              size="small"
              icon={isOpen ? 'chevron-down' : 'chevron-right'}
            />
          </Tooltip>
          {summary}
        </Summary>
        <Details $size={size} $noContentMargin={noContentMargin}>
          {typeof children === 'function' ? children({ isOpen }) : children}
        </Details>
      </StyledAccordion>
    </Container>
  )
}

const Container = styled.div`
  border: 1px solid ${theme.palette.grey[400]};
  border-radius: 12px;
`

const StyledAccordion = styled(MuiAccordion)`
  border-radius: 12px;
  overflow: hidden;

  &.MuiAccordion-root.MuiPaper-root {
    border-radius: 12px;
    background-color: transparent;
  }
  &.MuiAccordion-root {
    overflow: inherit;

    &:before {
      height: 0;
    }
    &.Mui-expanded {
      margin: 0;
    }
  }

  .MuiAccordionSummary-content {
    width: 100%;
  }
`

const Summary = styled(AccordionSummary)<{ $size?: AccordionSize }>`
  && {
    height: ${({ $size }) => ($size === AccordionSizeEnum.medium ? NAV_HEIGHT : 92)}px;
    border-radius: 12px;

    &.Mui-expanded {
      border-radius: 12px 12px 0 0;
    }

    &.MuiAccordionSummary-root.Mui-focusVisible {
      background-color: inherit;
      box-shadow: 0px 0px 0px 4px ${theme.palette.primary[200]};
      &:hover {
        background-color: ${theme.palette.grey[100]};
      }
    }

    &:hover {
      background-color: ${theme.palette.grey[100]};
    }

    &:active {
      background-color: ${theme.palette.grey[200]};
    }

    .MuiAccordionSummary-content {
      display: flex;
      height: ${({ $size }) => ($size === AccordionSizeEnum.medium ? NAV_HEIGHT : 92)}px;
      box-sizing: border-box;
      align-items: center;
      padding: ${({ $size }) =>
        $size === AccordionSizeEnum.medium ? theme.spacing(4) : theme.spacing(8)};

      > *:first-child {
        margin-right: ${theme.spacing(3)};
      }
    }
  }
`

const Details = styled(AccordionDetails)<{ $size?: AccordionSize; $noContentMargin?: boolean }>`
  display: flex;
  flex-direction: column;
  box-shadow: ${theme.shadows[5]};

  &.MuiAccordionDetails-root {
    padding: ${({ $size, $noContentMargin }) =>
      $noContentMargin
        ? 0
        : $size === AccordionSizeEnum.medium
        ? theme.spacing(4)
        : theme.spacing(8)};
  }
`
